/* ─────────────────────────────────────────────────────────
 * Role-Based Access Control (RBAC) Module
 * Manages users, roles, invitations, and session state.
 * Data persisted in localStorage (swap for API later).
 * ───────────────────────────────────────────────────────── */

export type Role = 'super-admin' | 'it-admin' | 'qa' | 'devops' | 'developer' | 'helpdesk';

export const ROLE_LABELS: Record<Role, string> = {
  'super-admin': 'Super Admin',
  'it-admin': 'IT Admin',
  qa: 'QA',
  devops: 'DevOps',
  developer: 'Developer',
  helpdesk: 'Helpdesk',
};

/** Roles that can manage (invite / remove) other users */
export const ADMIN_ROLES: Role[] = ['super-admin', 'it-admin'];

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: 'active' | 'invited';
  createdAt: string;
  invitedBy?: string;
}

export interface Invitation {
  token: string;
  email: string;
  role: Role;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  accepted: boolean;
}

/* ── Storage Keys ── */
const USERS_KEY = 'learnwithai_admin_users';
const INVITATIONS_KEY = 'learnwithai_admin_invitations';
const SESSION_KEY = 'learnwithai_admin_session';
const SETUP_KEY = 'learnwithai_super_admin_setup';

/* ── Helpers ── */
function load<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) || '') as T; }
  catch { return fallback; }
}

function save<T>(key: string, data: T) {
  localStorage.setItem(key, JSON.stringify(data));
}

/* ── Super-Admin Setup ── */

/** Synchronous local check — only valid when the server-side check has already run. */
export function isSuperAdminSetup(): boolean {
  return localStorage.getItem(SETUP_KEY) === 'true';
}

/** Async server-side check — use this on the login/setup pages for the authoritative answer. */
export async function isSuperAdminSetupAsync(): Promise<boolean> {
  try {
    const res = await fetch('/api/admin/setup-status');
    if (!res.ok) return false;
    const data = await res.json() as { setupComplete?: boolean; SetupComplete?: boolean };
    const isSetup = data.setupComplete ?? data.SetupComplete ?? false;
    if (isSetup) localStorage.setItem(SETUP_KEY, 'true');
    else localStorage.removeItem(SETUP_KEY);
    return isSetup;
  } catch {
    return localStorage.getItem(SETUP_KEY) === 'true';
  }
}

/** Create the first super admin via the server-side API (DB-backed, not localStorage). */
export async function setupSuperAdminApi(email: string, name: string, password: string): Promise<AdminUser | null> {
  try {
    const res = await fetch('/api/admin/setup-super-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.toLowerCase().trim(), name: name.trim(), password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { Message?: string };
      throw new Error(err.Message ?? 'Setup failed');
    }
    const u = await res.json() as { id: string; email: string; name: string; role: string; createdAt: string; token: string };
    localStorage.setItem(SETUP_KEY, 'true');
    const user: AdminUser = { id: u.id, email: u.email, name: u.name, role: u.role as Role, status: 'active', createdAt: u.createdAt };
    if (u.token) setAdminJwt(u.token);
    setSession(user);
    return user;
  } catch (e) {
    throw e;
  }
}

/** @deprecated — use setupSuperAdminApi instead (localStorage-only, no DB). */
export function setupSuperAdmin(email: string, name: string): AdminUser {
  const user: AdminUser = {
    id: crypto.randomUUID(),
    email: email.toLowerCase().trim(),
    name: name.trim(),
    role: 'super-admin',
    status: 'active',
    createdAt: new Date().toISOString(),
  };
  save(USERS_KEY, [user]);
  localStorage.setItem(SETUP_KEY, 'true');
  setSession(user);
  return user;
}

/* ── Session ── */
export function setSession(user: AdminUser) {
  save(SESSION_KEY, user);
  localStorage.setItem('auth_token', `admin-${user.id}`);
}

export function getSession(): AdminUser | null {
  return load<AdminUser | null>(SESSION_KEY, null);
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem('auth_token');
  localStorage.removeItem('learnwithai_admin_creds'); // clear any legacy plain-text credential store
  clearAdminJwt();
}

/* ── Login ── */
// Synchronous localStorage login is removed — all admin auth is server-backed via adminLoginApi.
export function adminLogin(_email: string, _password: string): AdminUser | null {
  return null;
}

const ADMIN_JWT_KEY = 'learnwithai_admin_jwt';

export function getAdminJwt(): string | null {
  return localStorage.getItem(ADMIN_JWT_KEY);
}

function setAdminJwt(token: string) {
  localStorage.setItem(ADMIN_JWT_KEY, token);
}

function clearAdminJwt() {
  localStorage.removeItem(ADMIN_JWT_KEY);
}

/** Try logging in via DB-backed credential (for staff invited via the proper invite flow). */
export async function adminLoginApi(email: string, password: string): Promise<AdminUser | null> {
  try {
    const data = await fetch('/api/admin/staff-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.toLowerCase().trim(), password }),
    });
    if (!data.ok) return null;
    const u = await data.json() as { id: string; email: string; name: string; role: string; invitedBy: string; createdAt: string; token: string };
    const user: AdminUser = { id: u.id, email: u.email, name: u.name, role: u.role as Role, status: 'active', createdAt: u.createdAt, invitedBy: u.invitedBy };
    if (u.token) setAdminJwt(u.token);
    setSession(user);
    localStorage.setItem(SETUP_KEY, 'true');
    return user;
  } catch { return null; }
}

/* ── Users CRUD ── */
export function getUsers(): AdminUser[] {
  return load<AdminUser[]>(USERS_KEY, []);
}

export function removeUser(id: string) {
  const users = getUsers().filter(u => u.id !== id);
  save(USERS_KEY, users);
}

/* ── Invitations (localStorage fallback — kept for backward compat) ── */
export function getInvitations(): Invitation[] {
  return load<Invitation[]>(INVITATIONS_KEY, []);
}

export function revokeInvitation(token: string) {
  const invitations = getInvitations().filter(i => i.token !== token);
  save(INVITATIONS_KEY, invitations);
}

/* ── Invitations (API-backed — use these for cross-browser support) ── */
async function adminFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const jwt = getAdminJwt();
  const res = await fetch(`/api/admin${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { 'Authorization': `Bearer ${jwt}` } : {}),
      ...options?.headers,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

export async function createInvitation(email: string, role: Role, invitedByEmail: string): Promise<Invitation> {
  const data = await adminFetch<{ token: string; email: string; role: string; invitedBy: string; createdAt: string; expiresAt: string }>(
    '/staff-invites',
    { method: 'POST', body: JSON.stringify({ email, role, invitedBy: invitedByEmail }) },
  );
  return { token: data.token, email: data.email, role: data.role as Role, invitedBy: data.invitedBy, createdAt: data.createdAt, expiresAt: data.expiresAt, accepted: false };
}

export async function fetchInvitations(): Promise<Invitation[]> {
  const data = await adminFetch<Array<{ token: string; email: string; role: string; invitedBy: string; createdAt: string; expiresAt: string; accepted: boolean }>>('/staff-invites');
  return data.map(d => ({ token: d.token, email: d.email, role: d.role as Role, invitedBy: d.invitedBy, createdAt: d.createdAt, expiresAt: d.expiresAt, accepted: d.accepted }));
}

export async function revokeInvitationApi(token: string): Promise<void> {
  await adminFetch(`/staff-invites/${token}`, { method: 'DELETE' });
}

export async function fetchStaffUsers(): Promise<Array<{ id: string; email: string; name: string; role: string; invitedBy: string; createdAt: string; lastLoginAt: string | null }>> {
  return adminFetch('/staff-users');
}

export async function removeStaffUser(id: string): Promise<void> {
  await adminFetch(`/staff-users/${id}`, { method: 'DELETE' });
}

/** Fetch an invitation by token from the API — public endpoint, no auth required. */
export async function fetchInvitationByToken(token: string): Promise<Invitation | null> {
  try {
    const res = await fetch(`/api/admin/staff-invite/${token}`, { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json() as { token: string; email: string; role: string; invitedBy: string; createdAt: string; expiresAt: string };
    return { token: data.token, email: data.email, role: data.role as Role, invitedBy: data.invitedBy, createdAt: data.createdAt, expiresAt: data.expiresAt, accepted: false };
  } catch {
    return null;
  }
}

/** Accept an invitation via API — public endpoint, creates DB credential, returns AdminUser for session. */
export async function acceptInvitationApi(token: string, name: string, password: string): Promise<AdminUser | null> {
  try {
    const res = await fetch(`/api/admin/staff-invite/${token}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, password }),
    });
    if (!res.ok) return null;
    const data = await res.json() as { id: string; email: string; name: string; role: string; invitedBy: string; createdAt: string };
    const user: AdminUser = { id: data.id, email: data.email, name: data.name, role: data.role as Role, status: 'active', createdAt: data.createdAt, invitedBy: data.invitedBy };
    setSession(user);
    return user;
  } catch {
    return null;
  }
}

/* ── Permission checks ── */
export function canManageUsers(role: Role): boolean {
  return ADMIN_ROLES.includes(role);
}

export function canAccessAdmin(role: Role): boolean {
  return Object.keys(ROLE_LABELS).includes(role);
}
